/**
 * @file Shared HTTP and application constants (base URL, routes, storage keys, limits).
 * @module constants/api
 *
 * @description
 * Central place for API path segments, defaults used by services and hooks, and
 * lightweight UI-related constants. Base URL resolution follows {@link getEnv}.
 *
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */

import { getEnv } from '../config/env';

/** Keep `version` in sync with `package.json` when cutting releases. */
export const APP_META = {
  name: 'Jiggle Lead Management',
  description:
    'Lead management dashboard: authentication, campaigns, advertisers, leads, exports, and settings.',
  email: 'sanjay.jangid@dgtoohl.com',
  date: '2026-05-10',
  version: '0.0.0',
} as const;

/** Resolved API origin (from `VITE_*` env via {@link getEnv}). No trailing slash. */
export const API_BASE_URL = getEnv().apiBaseUrl;

/**
 * Relative path segments appended after `API_BASE_URL` (no leading slash).
 * Full URLs are built as `${API_BASE_URL}/${API_ENDPOINTS.KEY}`.
 */
export const API_ENDPOINTS = {
  /** POST — auth */
  LOGIN: 'login',

  /** GET — dashboard KPIs / charts */
  DASHBOARD: 'dashboard',

  /** GET — paginated leads; query: advertiser_id, campaign_id, source, page, data_type, … */
  LEADS_VIEW: 'view-leads',
  /** POST — multipart CSV upload */
  // LEADS_UPLOAD: 'leads-upload/upload',
  LEADS_UPLOAD: 'leads-upload/upload-async',
  /** POST — latest async upload job + progress */
  LEADS_UPLOAD_LATEST_JOB: 'leads-upload/latest-job',
  /** GET — single upload job status by id (`…/status/{id}`) */
  LEADS_UPLOAD_STATUS: 'leads-upload/status',
  /** GET — paginated duplicate upload files; GET …/{filename} — download duplicate file */
  LEADS_UPLOAD_DOWNLOAD_DUPLICATES: 'leads-upload/download-duplicates',
  /** POST — export job */
  LEADS_EXPORT: 'export',

  /** GET paginated table / POST create */
  SOURCES: 'sources',
  /** GET — source select fields only (Campaigns, FilterBar, Leads) */
  SOURCES_LIST: 'sources/list',
  /** PUT / DELETE — source by id: sources/{id} */
  sourceById: (id: string | number) => `sources/${id}`,
  /** POST — set source status: sources/{id}/status/{active|inactive} */
  sourceStatusPath: (id: string | number, status: 'active' | 'inactive') =>
    `sources/${id}/status/${status}`,

  CAMPAIGNS: 'campaigns',
  /** GET — campaigns for one advertiser: campaigns/advertiser/{id} */
  campaignsByAdvertiser: (advertiserId: string | number) =>
    `campaigns/advertiser/${advertiserId}`,
  ADVERTISERS: 'advertisers',
  /** GET — advertiser select dropdown options */
  ADVERTISERS_LIST: 'advertisers/list',

  SETTINGS: 'settings',
  HISTORY: 'upload-history',
  /** GET — all lead upload files; GET …/{id} — file by id */
  LEAD_UPLOAD_FILES: 'lead-upload-files',
  leadUploadFileById: (id: string | number) => `lead-upload-files/${id}`,
  EXPORT_LOG_DAYS: 'settings/export-log-days',
  EXPORT_HISTORY: 'settings/export-history',
  SETTINGS_TIME_ZONE: 'settings/time-zone',
} as const;

export type ApiEndpointKey = keyof typeof API_ENDPOINTS;

export const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  DELETE: 'DELETE',
} as const;

export const STORAGE_KEYS = {
  TOKEN: 'jiggle_token',
  USER: 'jiggle_user',
} as const;

export const PAGINATION = {
  DEFAULT_PER_PAGE: 10,
  MAX_PER_PAGE: 100,
} as const;

export const UPLOAD = {
  MAX_FILE_SIZE: 10 * 1024 * 1024,
  ALLOWED_TYPES: ['.csv', '.xlsx', '.xls'],
} as const;

export const CHART_COLORS = {
  PRIMARY: 'rgb(59, 130, 246)',
  SECONDARY: 'rgb(20, 184, 166)',
  SUCCESS: 'rgb(34, 197, 94)',
  WARNING: 'rgb(249, 115, 22)',
  DANGER: 'rgb(239, 68, 68)',
  PURPLE: 'rgb(168, 85, 247)',
} as const;

export const STATUS_TYPES = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

export const EXPORT_FORMATS = {
  CSV: 'csv',
  EXCEL: 'excel',
} as const;
